import { buildSearchText } from "./search-text";
import type {
  Company,
  CompanyLocation,
  CompanyNote,
  ContactChannel,
  Person,
  Product,
  Sample,
} from "@/types";

/**
 * What goes into each record's `searchText`.
 *
 * Mirrors the app's repositories (`company_repository.dart`,
 * `product_repository.dart`). The parts are not arbitrary: a company's search
 * field carries its people, addresses and phone numbers as well as its own
 * name, which is what lets one query over the companies list find a supplier
 * by the mobile number printed on their card.
 *
 * Getting this wrong fails silently in the worst way. The record saves, syncs
 * and displays perfectly — and is simply never found again, here or on any
 * phone, by anyone who does not already know where it is.
 */

/** Tombstoned children are not part of what a company can be found by. */
const live = <T extends { deletedAt: string | null }>(rows: T[]) =>
  rows.filter((row) => row.deletedAt == null);

export function companySearchText(
  company: Pick<Company, "name" | "notes">,
  children: {
    locations: CompanyLocation[];
    channels: ContactChannel[];
    persons: Person[];
    notes: CompanyNote[];
  }
): string {
  return buildSearchText([
    company.name,
    company.notes,
    ...live(children.locations).flatMap((row) => [row.address, row.city, row.country]),
    ...live(children.channels).map((row) => row.value),
    ...live(children.persons).flatMap((row) => [row.name, row.title, row.phone, row.email]),
    // The notes belong here for the same reason the app puts them here: they
    // are where "the ones who came down to 2.10" is written. Leaving them out
    // would be worse than never adding them — this function *rewrites* the
    // field, so a company edited from the panel would silently lose every note
    // from its search index.
    ...live(children.notes).flatMap((row) => [row.title, row.body]),
  ]);
}

export const personSearchText = (person: Pick<Person, "name" | "title" | "phone" | "email" | "notes">) =>
  buildSearchText([person.name, person.title, person.phone, person.email, person.notes]);

/**
 * Products and samples carry their company's *name* in their own search field,
 * so that "acme vida" finds the product without a join. The cost is that
 * renaming a company has to re-stamp every one of them — see `renameFanOut`
 * in the company page.
 */
export const productSearchText = (
  product: Pick<Product, "name" | "code" | "description">,
  companyName: string
) => buildSearchText([product.name, product.code, product.description, companyName]);

export const sampleSearchText = (
  sample: Pick<Sample, "name" | "code" | "notes" | "storageLocation">,
  companyName: string
) =>
  buildSearchText([
    sample.name,
    sample.code,
    sample.notes,
    sample.storageLocation,
    companyName,
  ]);
